import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
  Chip,
  Grid,
  Skeleton,
} from '@mui/material';
import { shopWallClockDate } from '../../../utils/shopTime';
import { Appointment, ShopSettings } from '../../../types';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';
import { formatTimeForDisplay } from './helpers';

interface RescheduleDialogProps {
  rescheduleDialogOpen: boolean;
  selectedAppointment: Appointment | null;
  selectedDate: Date;
  selectedTimeSlot: string;
  availableTimeSlots: string[];
  isLoadingTimeSlots: boolean;
  slotBookingCounts: Record<string, number>;
  shopSettings: ShopSettings | null;
  timeSlotError: string | null;
  setTimeSlotError: (message: string | null) => void;
  isDateAvailable: (date: Date) => boolean;
  tzOf: (shopId?: string | null) => string | undefined;
  formatAppointmentDateTime: (appointment: Appointment) => string;
  handleDateChange: (date: Date | null) => void;
  handleTimeSlotSelect: (timeSlot: string) => void;
  handleRescheduleDialogClose: () => void;
  handleSaveReschedule: () => void;
}

/** Move an appointment to another day and slot, showing how full each slot is. */
const RescheduleDialog: React.FC<RescheduleDialogProps> = ({
  rescheduleDialogOpen,
  selectedAppointment,
  selectedDate,
  selectedTimeSlot,
  availableTimeSlots,
  isLoadingTimeSlots,
  slotBookingCounts,
  shopSettings,
  timeSlotError,
  setTimeSlotError,
  isDateAvailable,
  tzOf,
  formatAppointmentDateTime,
  handleDateChange,
  handleTimeSlotSelect,
  handleRescheduleDialogClose,
  handleSaveReschedule,
}) => {
  return (
    <Dialog open={rescheduleDialogOpen} onClose={handleRescheduleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>Reschedule Appointment</DialogTitle>
      <DialogContent>
        {selectedAppointment && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Customer: {selectedAppointment.customerName}
            </Typography>
            <Typography variant="body2" gutterBottom>
              Current Schedule: {formatAppointmentDateTime(selectedAppointment)}
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="New Date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  disablePast
                  shouldDisableDate={isDateAvailable ? (date) => !isDateAvailable(date) : undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Selected Date: <strong>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</strong>
                </Typography>
                <Typography variant="body2">
                  Selected Time: <strong>{selectedTimeSlot ? formatTimeForDisplay(selectedTimeSlot) : 'Not selected'}</strong>
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Select New Time:
                </Typography>
                
                {/* Legend for time slot statuses */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label="Available"
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Available
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label="Selected"
                      color="primary"
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Selected
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label="Current"
                      size="small"
                      sx={{ 
                        mr: 1, 
                        border: '1px dashed',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Current Time
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label="Booked"
                      disabled
                      size="small"
                      sx={{ 
                        mr: 1, 
                        opacity: 0.8,
                        backgroundColor: 'rgba(75, 75, 75, 0.25)',
                        border: '1px solid rgba(120, 120, 120, 0.5)',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          width: '100%',
                          height: '1px',
                          backgroundColor: 'rgba(180, 180, 180, 0.8)',
                          left: 0,
                          top: '50%',
                        },
                        '& .MuiChip-label': {
                          color: 'rgba(200, 200, 200, 0.9)',
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Fully Booked (Max: {shopSettings?.maxConcurrentBookings || 1})
                    </Typography>
                  </Box>
                </Box>
                
                {isLoadingTimeSlots ? (
                  <Box sx={{ mt: 2 }}>
                    <Skeleton variant="rectangular" height={60} />
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {availableTimeSlots.length === 0 ? (
                      <Alert severity="warning">
                        No available time slots for this date. Please select another date.
                      </Alert>
                    ) : (
                      // Display all time slots but show booked/partial/full ones accordingly
                      availableTimeSlots.map((slot) => {
                        // --- NEW LOGIC START ---
                        const bookingCount = slotBookingCounts[slot] || 0;
                        const maxBookings = shopSettings?.maxConcurrentBookings || 1;
                        const isFullyBooked = bookingCount >= maxBookings;
                        const isPartiallyBooked = bookingCount > 0 && bookingCount < maxBookings;
                        const isSelected = selectedTimeSlot === slot;
                        const isCurrentAppointmentTime = selectedAppointment &&
                          selectedAppointment.appointmentTime.substring(0, 5) === slot &&
                          selectedAppointment.appointmentDate &&
                          format(selectedDate, 'yyyy-MM-dd') === format(shopWallClockDate(selectedAppointment.appointmentDate, tzOf(selectedAppointment.shopId)), 'yyyy-MM-dd');
                        // Allow selection if not fully booked or if it's the current appointment's slot
                        const allowSelection = !isFullyBooked || isCurrentAppointmentTime;
                        // --- NEW LOGIC END ---

                        return (
                          <Chip
                            key={slot}
                            label={formatTimeForDisplay(slot)}
                            onClick={allowSelection ? () => handleTimeSlotSelect(slot) : undefined}
                            onMouseDown={(e) => {
                              if (!allowSelection) {
                                e.preventDefault();
                                setTimeSlotError(`Time slot ${formatTimeForDisplay(slot)} is fully booked. Please select another time.`);
                              }
                            }}
                            color={isSelected ? 'primary' : isPartiallyBooked ? 'warning' : isFullyBooked ? 'default' : 'success'}
                            variant={isSelected ? 'filled' : 'outlined'}
                            disabled={isFullyBooked && !isCurrentAppointmentTime}
                            sx={{ 
                              m: 0.5,
                              opacity: isFullyBooked && !isCurrentAppointmentTime ? 0.8 : 1,
                              backgroundColor: isCurrentAppointmentTime && !isSelected
                                ? 'rgba(25, 118, 210, 0.08)'
                                : isFullyBooked && !isCurrentAppointmentTime 
                                  ? 'rgba(75, 75, 75, 0.25)' 
                                  : isPartiallyBooked
                                    ? 'rgba(255, 193, 7, 0.15)'
                                    : undefined,
                              color: isCurrentAppointmentTime && !isSelected
                                ? 'primary.main'
                                : isFullyBooked && !isCurrentAppointmentTime 
                                  ? '#aaa'
                                  : undefined,
                              borderColor: isCurrentAppointmentTime && !isSelected
                                ? 'primary.main'
                                : isFullyBooked && !isCurrentAppointmentTime 
                                  ? 'rgba(120, 120, 120, 0.5)'
                                  : undefined,
                              border: isCurrentAppointmentTime && !isSelected ? '1px dashed' : undefined,
                              '&.MuiChip-colorPrimary': {
                                fontWeight: 'bold',
                              },
                              '&:hover': {
                                backgroundColor: isCurrentAppointmentTime && !isSelected
                                  ? 'rgba(25, 118, 210, 0.12)'
                                  : isFullyBooked && !isCurrentAppointmentTime
                                    ? 'rgba(75, 75, 75, 0.25)'
                                    : isPartiallyBooked
                                      ? 'rgba(255, 193, 7, 0.22)'
                                      : undefined,
                                cursor: isFullyBooked && !isCurrentAppointmentTime 
                                  ? 'not-allowed' 
                                  : 'pointer',
                              },
                              position: 'relative',
                              '&::after': isFullyBooked && !isCurrentAppointmentTime ? {
                                content: '""',
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                width: '100%',
                                height: '1px',
                                backgroundColor: 'rgba(180, 180, 180, 0.8)',
                              } : {},
                              '& .MuiChip-label': {
                                color: isFullyBooked && !isCurrentAppointmentTime 
                                  ? 'rgba(200, 200, 200, 0.9)'
                                  : undefined
                              }
                            }}
                          />
                        );
                      })
                    )}
                  </Box>
                )}
                
                {/* Display time slot selection error */}
                {timeSlotError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {timeSlotError}
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRescheduleDialogClose}>Cancel</Button>
        <Button 
          onClick={handleSaveReschedule} 
          variant="contained"
          disabled={!selectedTimeSlot}
        >
          Reschedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleDialog;
